import * as React from 'react';
import {
  createFileRoute,
  SearchSchemaInput,
  useCanGoBack,
  useRouter, useNavigate
} from '@tanstack/react-router';
import PageLoader from '../../../../microcomponents/PageLoader';
import {
  DatePicker,
  Form,
  Input,
  message,
  Segmented,
  Select,
  TimePicker
} from 'antd';
import { useState } from 'react';
import { apiV1 } from '../../../../utils/apiClient';
import { supabase } from '../../../../utils/supabase';
import { compressImage } from '../../../../utils/imageCompression';
import { trackEvent } from '../../../../utils/Matomo/tracking_functions';
import {
  FacebookOutlined,
  InstagramOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined, WhatsAppOutlined
} from '@ant-design/icons';
import ImageUploadInput from '../../../../components/ImageUploadInput';
import ConfirmButton from '../../../../components/ConfirmButton';
import PhonePreviewFrame from '../../../../components/PhonePreviewFrame';
import NotificationPreview from '../../../../components/NotificationPreview';
import useShopAttrs from '../../../../utils/hooks/useShopAttrs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CallToActionType, ctaDefaultLabel,
  ctaValueInputLabels, ctaValueValidation,
  NewNotification, NotificationWidget, ScheduleNotification, ShopNewsRecipient, shopNewsRecipientSchema
} from '@next-is-free-monorepo/next-is-free-types';
import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import NotificationScheduleModal from '../../../../components/notifications/NotificationScheduleModal';
import Wrapper from '../../../../components/Wrapper';
import BgIcon from '../../../../microcomponents/BgIcon';
import {
  IconClock,
  IconGift,
  IconInfinity,
  IconInfoCircle,
  IconX
} from '@tabler/icons-react';
import { v4 as uuidv4 } from 'uuid';
import CustomersAutocomplete from '../../../../components/customers/CustomersAutocomplete';
import GroupsAutocomplete from '../../../../components/customers/GroupsAutocomplete';
import { planFeatures } from '@next-is-free-monorepo/next-is-free-shared-utils';
import PlanGate, { PlanGateRef } from '../../../../components/PlanGate';
import { useRef } from 'react';
import { NButton } from '@next-is-free-monorepo/nif-web-components';
import { CalendarIcon, MailSendIconFreeIcons, PlusSignIcon, SentIcon } from '@hugeicons/core-free-icons';
import BackButton from '../../../../microcomponents/BackButton';
import { usePendingNotificationsStore } from '../../../../stores/pendingNotificationsStore';

const { TextArea } = Input;

export const Route = createFileRoute('/_authenticated/notifications/new')({
  component: RouteComponent,
  pendingComponent: PageLoader,
  validateSearch: (
    input: {
      scheduleTime?: string
      recipients?: ShopNewsRecipient
    } & SearchSchemaInput
  ) => {
    return {
      scheduleTime: z.string().optional().parse(input?.scheduleTime),
      recipients: shopNewsRecipientSchema.optional().parse(input?.recipients)
    };
  }
});

function RouteComponent() {
  const canGoBack = useCanGoBack();
  const navigate = useNavigate();
  const router = useRouter();
  const safeGoBack = canGoBack ? useRouter().history.back : () => navigate({ to: '/notifications' });
  const shopAttrs = useShopAttrs();
  const queryClient = useQueryClient();
  const { scheduleTime: scheduleTimeFromSearch, recipients: recipientsFromSearch } = Route.useSearch();

  const [form] = Form.useForm();
  const [image, setImage] = useState();
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [withOneTimePromotion, setWithOneTimePromotion] = useState(false);
  const planGateRef = useRef<PlanGateRef>(null);
  const targetedNotificationsPlanGateRef = useRef<PlanGateRef>(null);

  const title = Form.useWatch('title', form);
  const text = Form.useWatch('text', form);
  const ctaType: CallToActionType | 'no-cta' = Form.useWatch('cta-type', form);
  const ctaLabel: string = Form.useWatch('cta-label', form);
  const ctaValue: string = Form.useWatch('cta-value', form);
  const oneTimePromotionHasDeadline: boolean = Form.useWatch('oneTimePromotionHasDeadline', form);
  const oneTimePromoDescription: string = Form.useWatch('oneTimePromoDescription', form);
  const recipientType: ShopNewsRecipient['type'] = Form.useWatch('recipientsType', form);
  const recipients: ShopNewsRecipient['recipients'] = Form.useWatch('recipients', form);

  type formValues = {
    title: string,
    text: string,
    'cta-type': CallToActionType | 'no-cta',
    'cta-label': string,
    'cta-value': string,
    oneTimePromotionHasDeadline: boolean,
    oneTimePromoDescription: string | undefined,
    oneTimePromotionValidFromDate: Dayjs | undefined,
    oneTimePromotionValidFromTime: Dayjs | undefined,
    oneTimePromotionValidToDate: Dayjs | undefined,
    oneTimePromotionValidToTime: Dayjs | undefined,
    recipients: ShopNewsRecipient['recipients'],
    recipientsType: ShopNewsRecipient['type']
  }

  function onFormChange(changedValues: any, values: any) {
    if (changedValues['cta-type']) {
      const ctaType: CallToActionType = changedValues['cta-type'];
      form.setFieldValue('cta-label', ctaDefaultLabel[ctaType]);
      form.setFieldValue('cta-value', '');
    }
  }

  function handleCtaTypeChange(value: CallToActionType | 'no-cta') {
    // Check if user has callToActions feature
    const hasCallToActions = planFeatures(shopAttrs.data.subscription_plan, 'callToActions');

    // If trying to select a CTA option (not "no-cta") and doesn't have the feature
    if (value !== 'no-cta' && !hasCallToActions) {
      // Show upgrade modal instead of selecting the option
      planGateRef.current?.triggerUpgradeModal();
      // Keep the form value as 'no-cta'
      form.setFieldValue('cta-type', 'no-cta');
      return;
    }

    // Normal selection flow
    form.setFieldValue('cta-type', value);
    if (value !== 'no-cta') {
      form.setFieldValue('cta-label', ctaDefaultLabel[value]);
      form.setFieldValue('cta-value', '');
    }
  }

  function handleRecipientsTypeChange(value: ShopNewsRecipient['type']) {
    // Check if user has targetedNotifications feature
    const hasTargetedNotifications = planFeatures(shopAttrs.data.subscription_plan, 'targetedNotifications');

    // If trying to select targeted options (not "ALL_USERS") and doesn't have the feature
    if (value !== 'ALL_USERS' && !hasTargetedNotifications) {
      // Show upgrade modal instead of selecting the option
      targetedNotificationsPlanGateRef.current?.triggerUpgradeModal();
      // Keep the form value as 'ALL_USERS'
      form.setFieldValue('recipientsType', 'ALL_USERS');
      return;
    }

    // Normal selection flow
    form.setFieldValue('recipientsType', value);
    // Clear recipients when changing type
    form.setFieldValue('recipients', []);
  }

  async function sendNotificationFn(values: formValues) {


    const notification_id = uuidv4();

    if (image) {
      const compressedImage = await compressImage(image);
      const {
        data,
        error: uploadError
      } = await supabase.storage.from('main').upload(shopAttrs.data.id + '/' + notification_id + '.jpeg', compressedImage, { upsert: true });
      if (uploadError) {
        throw uploadError;
      }
      trackEvent('Notifications', 'Image Uploaded', values.title);
    }

    const widgets: NotificationWidget[] = [];

    let valid_from = undefined;
    let valid_to = undefined;

    if (oneTimePromotionHasDeadline && values.oneTimePromotionValidFromDate && values.oneTimePromotionValidFromTime && values.oneTimePromotionValidToDate && values.oneTimePromotionValidToTime) {
      valid_from = dayjs(values.oneTimePromotionValidFromDate).set('hour', values.oneTimePromotionValidFromTime.hour()).set('minute', values.oneTimePromotionValidFromTime.minute()).toISOString();
      valid_to = dayjs(values.oneTimePromotionValidToDate).set('hour', values.oneTimePromotionValidToTime.hour()).set('minute', values.oneTimePromotionValidToTime.minute()).toISOString();
    }

    if (withOneTimePromotion) {
      widgets.push({
        type: 'one_time_promo',
        options: {
          promo_id: uuidv4(),
          description: oneTimePromoDescription,
          valid_from,
          valid_to
        }
      });
    }

    const body: NewNotification = {
      id: notification_id,
      title: values.title,
      text: values.text,
      image: !!image,
      business_name: shopAttrs.data.business_name,
      shop_latest_update_at: shopAttrs.data.latest_update_at ?? '',
      call_to_actions: ctaType === 'no-cta' ? null : [{
        type: ctaType,
        value: ctaValue,
        label: ctaLabel
      }],
      widgets,
      brand_color: shopAttrs.data.brand_color,
      recipients: { type: values.recipientsType, recipients: values.recipients ?? [] }
    };

    await apiV1.post('/shop/notifications', body);
    return notification_id;
  }

  const {
    addPendingNotification
  } = usePendingNotificationsStore(state => state);

  const send = useMutation({
    mutationKey: ['notifications', 'send'],
    mutationFn: sendNotificationFn,
    onSuccess: async (data) => {
      await navigate({ to: '/notifications' })
      await queryClient.invalidateQueries({ queryKey: ['get', '/shop/notifications'] });
      addPendingNotification(data);
    },
    onError: (e) => {
      console.log(e);
      message.error('Errore nell\'invio della notifica');
    }
  });

  async function scheduleNotification(scheduleTime: Dayjs) {

    const values = form.getFieldsValue() as formValues;

    const notification_id = uuidv4();

    if (image) {
      const compressedImage = await compressImage(image);
      const {
        data,
        error: uploadError
      } = await supabase.storage.from('main').upload(shopAttrs.data.id + '/' + notification_id + '.jpeg', compressedImage, { upsert: true });
      if (uploadError) {
        throw uploadError;
      }
    }

    const widgets: NotificationWidget[] = [];

    let valid_from = undefined;
    let valid_to = undefined;

    if (oneTimePromotionHasDeadline && values.oneTimePromotionValidFromDate && values.oneTimePromotionValidFromTime && values.oneTimePromotionValidToDate && values.oneTimePromotionValidToTime) {
      valid_from = dayjs(values.oneTimePromotionValidFromDate).set('hour', values.oneTimePromotionValidFromTime.hour()).set('minute', values.oneTimePromotionValidFromTime.minute()).toISOString();
      valid_to = dayjs(values.oneTimePromotionValidToDate).set('hour', values.oneTimePromotionValidToTime.hour()).set('minute', values.oneTimePromotionValidToTime.minute()).toISOString();
    }

    if (withOneTimePromotion) {
      widgets.push({
        type: 'one_time_promo',
        options: {
          promo_id: uuidv4(),
          description: oneTimePromoDescription,
          valid_from,
          valid_to
        }
      });
    }

    const body: ScheduleNotification = {
      id: notification_id,
      title: title,
      text: text,
      image: !!image,
      business_name: shopAttrs.data.business_name,
      shop_latest_update_at: shopAttrs.data.latest_update_at ?? '',
      call_to_actions: ctaType === 'no-cta' ? null : [{
        type: ctaType,
        value: ctaValue,
        label: ctaLabel
      }],
      widgets,
      schedule_time: scheduleTime.toISOString(),
      brand_color: shopAttrs.data.brand_color,
      recipients: { type: values.recipientsType, recipients: values.recipients ?? [] }
    };

    await apiV1.post('/shop/notifications/schedule', body);
  }

  const schedule = useMutation({
    mutationKey: ['notifications', 'schedule'],
    mutationFn: scheduleNotification,
    onSuccess: () => {
      message.success('Notifica programmata');
      safeGoBack();
      queryClient.invalidateQueries({ queryKey: ['get', '/shop/notifications'] });
    }
  });


  return (
    <>
      <div style={{ marginBottom: 40 }}>
      </div>
      <div className={'customizePageW p-4'}>
        <>
          <div style={{ width: '100%', maxWidth: 400 }}>
            <BackButton fallBackRouteOptions={{ to: '/notifications' }} className={'mb-5'} />
            <Form onValuesChange={onFormChange}
                  initialValues={{
                    'cta-type': 'no-cta',
                    oneTimePromotionHasDeadline: false,
                    oneTimePromotionValidToDate: dayjs(),
                    oneTimePromotionValidToTime: dayjs(),
                    oneTimePromotionValidFromDate: dayjs(),
                    oneTimePromotionValidFromTime: dayjs(),
                    recipientsType: recipientsFromSearch?.type ?? 'ALL_USERS',
                    recipients: recipientsFromSearch?.recipients ?? []
                  }} form={form}
                  onFinish={send.mutate} layout={'vertical'}>
              <Form.Item label={'Immagine'}>
                <ImageUploadInput setImage={setImage} image={image} setImagePreviewUrl={setImagePreviewUrl}
                                  imagePreviewUrl={imagePreviewUrl} objectFit={'cover'} />
              </Form.Item>
              <Form.Item rules={[{ required: true, message: 'Le notifiche devono avere un titolo' }]} label={'Titolo'}
                         name={'title'}>
                <Input size={'large'} onKeyDown={e => e.key === 'Enter' ? e.preventDefault() : null} showCount
                       maxLength={100}
                       placeholder={'Titolo'} name={'title'} />
              </Form.Item>
              <Form.Item rules={[{ required: true, message: 'Le notifiche devono avere un testo' }]} label={'Testo'}
                         style={{ marginBottom: 30 }} name={'text'}>
                <TextArea size={'large'} showCount maxLength={2000} autoSize={{ minRows: 3, maxRows: 10 }}
                          placeholder={'Testo'}
                          name={'title'} />
              </Form.Item>
              {!withOneTimePromotion &&
                <NButton color={'tertiary'} className={'w-full mb-3'} hugeIcon={PlusSignIcon} size={'small'}
                         onClick={() => setWithOneTimePromotion(true)}>Aggiungi
                  Buono sconto</NButton>
              }
              {withOneTimePromotion &&
                <Wrapper p={15} style={{ marginBottom: 30 }}>
                  <div className={'flex justify-between items-center mb-3'}>
                    <div className={'flex gap-2 items-center'}>
                      <BgIcon icon={<IconGift />} color={'blue'} />
                    </div>
                    <IconX onClick={() => setWithOneTimePromotion(false)} className={'text-slate-500 cursor-pointer'} />
                  </div>
                  <Form.Item rules={[{ required: true, message: 'Non può essere vuoto' }]}
                             name={'oneTimePromoDescription'} label={'Descrizione'}>
                    <Input size={'large'} placeholder={'Testo promozione'} />
                  </Form.Item>
                  <Form.Item name={'oneTimePromotionHasDeadline'} valuePropName={'checked'}>
                    <Segmented
                      block
                      options={[{
                        value: false,
                        label: 'Senza scadenza',
                        icon: <IconInfinity className={'inline-block'} size={14} />
                      }, {
                        value: true,
                        label: 'Con scadenza',
                        icon: <IconClock className={'inline-block'} size={14} />
                      }]}
                      value={oneTimePromotionHasDeadline}
                    />
                  </Form.Item>
                  {oneTimePromotionHasDeadline &&
                    <>
                      <p>Valida da</p>
                      <div className={'flex justify-between gap-2'}>
                        <Form.Item rules={[{ required: true, message: 'Non può essere vuoto' }]}
                                   name={'oneTimePromotionValidFromDate'} className={'flex-1'}>
                          <DatePicker size={'large'} format={'DD/MM/YY'} className={'w-full'}
                                      placeholder={'Data inizio'} />
                        </Form.Item>
                        <Form.Item rules={[{ required: true, message: 'Non può essere vuoto' }]}
                                   name={'oneTimePromotionValidFromTime'} className={'flex-1'}>
                          <TimePicker size={'large'} format={'HH:mm'} className={'w-full'}
                                      placeholder={'Ora d\'inizio'} />
                        </Form.Item>
                      </div>
                      <p>Valida fino a</p>
                      <div className={'flex justify-between gap-2'}>
                        <Form.Item rules={[{ required: true, message: 'Non può essere vuoto' }]}
                                   name={'oneTimePromotionValidToDate'} className={'flex-1'}>
                          <DatePicker size={'large'} format={'DD/MM/YY'} className={'w-full'}
                                      placeholder={'Data fine'} />
                        </Form.Item>
                        <Form.Item rules={[{ required: true, message: 'Non può essere vuoto' }]}
                                   name={'oneTimePromotionValidToTime'} className={'flex-1'}>
                          <TimePicker size={'large'} format={'HH:mm'} className={'w-full'}
                                      placeholder={'Ora fine'} />
                        </Form.Item>
                      </div>
                    </>
                  }
                  <p className={'text-sm text-slate-500 flex gap-1'}><IconInfoCircle size={16} className={'w-10'} />Una
                    promozione indipendente dai punti che il cliente può utilizzare una sola volta. La promozione può
                    essere disponibile per sempre o solo in un determinato periodo di tempo.</p>
                </Wrapper>
              }
              <Form.Item label={'Azione'} name={'cta-type'}
                         tooltip={'Seleziona un\'azione che l\'utente può eseguire direttamente dalla notifica, come visitare un sito, chiamare un numero o aggiungere un evento al calendario. Questo aiuta a rendere la notifica più interattiva ed efficace.'}
              >
                <Select size={'large'} onChange={handleCtaTypeChange}>
                  <Select.Option value="call"><PhoneOutlined /> Chiama</Select.Option>
                  <Select.Option value="whatsapp"><WhatsAppOutlined /> WhatsApp</Select.Option>
                  <Select.Option value="url"><LinkOutlined /> Link</Select.Option>
                  <Select.Option value="instagram"><InstagramOutlined /> Instagram</Select.Option>
                  <Select.Option value="facebook"><FacebookOutlined /> Facebook</Select.Option>
                  <Select.Option value="email"><MailOutlined /> Email</Select.Option>
                  <Select.Option value="no-cta" style={{ color: 'gray' }}>Nessuna</Select.Option>
                </Select>
              </Form.Item>
              {ctaType && ctaType !== 'no-cta' &&
                <Form.Item name={'cta-label'} rules={[{ required: true, message: 'Non può essere vuoto' }]}
                           label={'Testo bottone'} required>
                  <Input size={'large'} placeholder={ctaDefaultLabel[ctaType]} />
                </Form.Item>
              }
              {ctaType && ctaType !== 'no-cta' &&
                <Form.Item name={'cta-value'} rules={[{
                  validator: (_, value) => {
                    if (ctaValueValidation[ctaType].schema.safeParse(value).success) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject(ctaValueValidation[ctaType].errorMessage);
                    }
                  }
                }]} label={ctaValueInputLabels[ctaType]} required>
                  <Input size={'large'} placeholder={''} />
                </Form.Item>
              }
              <Form.Item name={'recipientsType'} label={'Invia a'}>
                <Select size={'large'} onChange={handleRecipientsTypeChange}>
                  <Select.Option value={'ALL_USERS'}>Tutti</Select.Option>
                  <Select.Option value={'GROUPS'}>
                    Gruppi
                  </Select.Option>
                  <Select.Option value={'SINGLE_USERS'}>
                    Singoli
                  </Select.Option>
                </Select>
              </Form.Item>
              {recipientType === 'SINGLE_USERS' &&
                <Form.Item
                  name={'recipients'}
                  rules={[{
                    validator: (_, value) => {
                      if (!value || value.length === 0) {
                        return Promise.reject(new Error('Seleziona almeno un cliente'));
                      }
                      return Promise.resolve();
                    }
                  }]}
                >
                  <CustomersAutocomplete disableNoNotifications selectedCustomers={recipients ?? []}
                                         setSelectedCustomers={(users) => form.setFieldsValue({
                                           recipients: users
                                         })} />
                </Form.Item>
              }
              {recipientType === 'GROUPS' &&
                <Form.Item
                  name={'recipients'}
                  rules={[{
                    validator: (_, value) => {
                      if (!value || value.length === 0) {
                        return Promise.reject(new Error('Seleziona almeno un gruppo'));
                      }
                      return Promise.resolve();
                    }
                  }]}
                >
                  <GroupsAutocomplete selectedGroups={recipients ?? []}
                                      setSelectedGroups={(groups) => form.setFieldsValue({
                                        recipients: groups
                                      })} />
                </Form.Item>
              }
              <Form.Item style={{ marginTop: 30 }}>
                <ConfirmButton mainButtonProps={{ hugeIcon: SentIcon }} loading={send.isPending}
                               onConfirm={() => form.submit()} text={'Invia la notifica'}
                               message={recipientType === 'ALL_USERS'
                                 ? 'La notifica verrà inviata a tutti i tuoi clienti e non può essere modificata, vuoi continuare?'
                                 : recipientType === 'GROUPS'
                                   ? `La notifica verrà inviata ai ${recipients?.length || 0} gruppi selezionati e non può essere modificata, vuoi continuare?`
                                   : `La notifica verrà inviata ai ${recipients?.length || 0} clienti selezionati e non può essere modificata, vuoi continuare?`
                               }
                               okText={'Invia'} />
              </Form.Item>
              <PlanGate hideMask featureKey={'scheduleNotifications'}>
                <NButton color={'secondary'}
                         onClick={() => form.validateFields().then(() => setScheduleModalOpen(true))}
                         hugeIcon={CalendarIcon} className={'w-full mb-10'}>
                  Programma l'invio
                </NButton>
              </PlanGate>
            </Form>
          </div>
          <div className={'w-full max-w-[350px] hidden md:block'}>
            <PhonePreviewFrame>
              <NotificationPreview notificationStatus={'pending'} widgets={withOneTimePromotion ? [{
                type: 'one_time_promo',
                options: { promo_id: '', description: oneTimePromoDescription }
              }] : undefined} scale={1.2}
                                   ctaValue={ctaValue} ctaType={ctaType} ctaLabel={ctaLabel}
                                   imageUrl={imagePreviewUrl}
                                   title={title}
                                   text={text} />
            </PhonePreviewFrame>
          </div>
        </>
        <NotificationScheduleModal open={scheduleModalOpen} onSubmit={schedule.mutate} loading={schedule.isPending}
                                   onCancel={() => setScheduleModalOpen(false)}
                                   defaultValue={scheduleTimeFromSearch ? dayjs(scheduleTimeFromSearch) : undefined} />
      </div>
      <PlanGate ref={planGateRef} featureKey="callToActions" hideMask>
        <div style={{ display: 'none' }} />
      </PlanGate>
      <PlanGate ref={targetedNotificationsPlanGateRef} featureKey="targetedNotifications" hideMask>
        <div style={{ display: 'none' }} />
      </PlanGate>
    </>);
}
